import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {HeartBGWhiteIcon, HeartSolidIcon} from '../icons'
import {useIntl} from 'react-intl'

/* Components */
import {IconButton, useMultiStyleConfig} from '@chakra-ui/react'
import {
    useAddProductToWishList,
    useIsProductInWishlist,
    useRemoveProductFromWishList
} from '../../hooks/use-wish-list'
import useOcapiSite from '../../hooks/useOcapiSite'
import {trackAddToWishlist} from '../../tracking/analytics'
import useProductItemsDetail from '../../hooks/useProductItemsDetail'
import {HIDE_WISHLIST_TOGGLE} from '../../constants'
import {Button as Chakra3Button} from '@chakra-ui/react3'
import {FavoriteFilledIcon, FavoriteIcon} from '../../leap/components/icons'

const hideWishlistToggle = (productID) => {
    for (let index = 0; index < HIDE_WISHLIST_TOGGLE.length; index++) {
        let pid = HIDE_WISHLIST_TOGGLE[index]

        if (pid === productID) {
            return true
        }

        if (pid.endsWith('*')) {
            let groupPid = pid.replace('*', '')

            if (productID.startsWith(groupPid)) {
                return true
            }
        }
    }

    return false
}

const WishlistToggle = (props) => {
    const {isDisabled, product, variant} = props
    const {wishlistEnabled} = useOcapiSite().preferences
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('WishlistToggle', props)
    const isInWishlist = useIsProductInWishlist(product?.id)
    const [hoveredHeart, setHoveredHeart] = useState(false)
    const [showUnfilledHeart, setShowUnfilledHeart] = useState(false)
    const {data: productItemsDetail} = useProductItemsDetail(props.basket)

    const {mutate: addToWishlist, isLoading: isAdding} = useAddProductToWishList({
        onSuccess: () => {
            if (variant === 'cart') {
                trackAddToWishlist(product, props.basket, productItemsDetail)
            }
            if (['imageGallery', 'productTile', 'cockpitGallery'].includes(variant)) {
                trackAddToWishlist(product, props.basket, productItemsDetail)
            }
        }
    })
    const {mutate: removeFromWishlist, isLoading: isRemoving} = useRemoveProductFromWishList()
    const isLoading = isAdding || isRemoving
    const hideWishlist = useMemo(() => hideWishlistToggle(product.id), [product])

    if (!wishlistEnabled || hideWishlist) {
        return null
    }

    const onClick = (e) => {
        e?.stopPropagation?.()
        e?.preventDefault?.()
        if (isInWishlist) {
            if (hoveredHeart) {
                setShowUnfilledHeart(true)
            }
            removeFromWishlist(product.id)
        } else {
            addToWishlist(product.id)
        }
        props.onClick?.()
    }

    const ariaLabel = isInWishlist
        ? formatMessage({
              id: 'global.remove_product_from_wishlist',
              defaultMessage: 'Remove product from wishlist'
          })
        : formatMessage({
              id: 'global.add_product_to_wishlist',
              defaultMessage: 'Add product to wishlist'
          })

    return variant === 'cockpitGallery' ? ( //xleap Chakra3
        <Chakra3Button
            variant="mediaFilled"
            // force icon only due to the loading rendering two children
            size="md-icon-only"
            aria-label={ariaLabel}
            loading={isLoading}
            onClick={onClick}
            disabled={isDisabled || isLoading || isAdding || isRemoving}
            pointerEvents="auto"
        >
            {isInWishlist ? (
                <FavoriteFilledIcon data-testid={'heart-icon_in-wishlist'} />
            ) : (
                <FavoriteIcon data-testid={'heart-icon_not-in-wishlist'} />
            )}
        </Chakra3Button>
    ) : (
        <IconButton
            aria-label={ariaLabel}
            isDisabled={isDisabled || isLoading}
            icon={
                <>
                    {isInWishlist ? (
                        <HeartSolidIcon width={6} data-testid={'heart-solid-icon'} />
                    ) : (
                        <>
                            {showUnfilledHeart ? (
                                <HeartBGWhiteIcon
                                    width={6}
                                    data-testid={'heart-icon_not-in-wishlist'}
                                />
                            ) : (
                                <>
                                    <HeartBGWhiteIcon
                                        data-not-hovered="button"
                                        width={6}
                                        data-testid={'heart-icon_not-in-wishlist'}
                                    />
                                    <HeartSolidIcon
                                        data-hovered="button"
                                        width={6}
                                        data-testid={'heart-icon_in-wishlist'}
                                    />
                                </>
                            )}
                        </>
                    )}
                </>
            }
            onClick={onClick}
            isLoading={isLoading}
            onMouseEnter={() => {
                setHoveredHeart(true)
            }}
            onMouseLeave={() => {
                setHoveredHeart(false)
                setShowUnfilledHeart(false)
            }}
            onBlur={() => {
                if (!hoveredHeart) {
                    setShowUnfilledHeart(false)
                }
            }}
            disabled={isAdding || isRemoving}
            pointerEvents="auto"
            {...styles.button}
        />
    )
}

WishlistToggle.propTypes = {
    isDisabled: PropTypes.bool,
    product: PropTypes.object,
    variant: PropTypes.string,
    basket: PropTypes.object,
    onClick: PropTypes.func
}

export default WishlistToggle
